import React, { useMemo, useState } from 'react';
import { View, StyleSheet, TouchableOpacity, StatusBar } from 'react-native';
import { Text } from '@/components/common/text';
import { useRoute, useNavigation } from '@react-navigation/native';
import type { StackScreenProps } from '@react-navigation/stack';
import type { RootStackParamList } from '@/navigation/RootNavigator';
import { usePaymentDesignTokens, PAYMENT_TYPOGRAPHY, PAYMENT_SPACING } from '@/constants/residents/mobile/paymentDesignTokens';
import { useGetLeaseQuery } from '@/store/api';
import { usePaymentBalance } from '@/features/payments/api/paymentHooks';
import { useLeaseHistory } from '@/features/payments/hooks/useLeaseHistory';
import type { HistoryRow } from '@/features/payments/hooks/useLeaseHistory';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useAppSelector } from '@/store';
import { ResidentMobileHeaderWithMenu } from '@/components/residents/mobile/ResidentMobileHeaderWithMenu';
import { MobileScreenLayout } from '@/components/residents/mobile/MobileScreenLayout';
import { BackLink } from '@/components/residents/mobile/BackLink';
import { useResidentScreen } from '@/hooks/residents/useResidentScreen';
import { MobilePaymentCardV2 } from '@/features/payments/components/mobile/MobilePaymentCardV2';
import { MobileTransactionHistoryTable } from '@/features/payments/components/mobile/MobileTransactionHistoryTable';
import { TransactionDetailsModal } from '@/features/payments/components/mobile/TransactionDetailsModal';
import type { RecurringCharge } from '@/features/payments/components/mobile/RecurringChargesModal';
import { selectLease, hasAutoPay } from '@/features/payments/utils/leaseAutoPay';


type MobilePaymentDetailScreenProps = StackScreenProps<RootStackParamList, 'PaymentDetail'>;

export const MobilePaymentDetailScreen: React.FC = () => {
  const pt = usePaymentDesignTokens();

  const styles = useMemo(() => StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: pt.BACKGROUND,
    },
    loadingContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
    },
    errorContainer: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      padding: PAYMENT_SPACING.xxl,
    },
    errorText: {
      ...PAYMENT_TYPOGRAPHY.bodySm,
      color: pt.TEXT_MUTED,
      textAlign: 'center',
      marginBottom: PAYMENT_SPACING.xl,
    },
    backButton: {
      backgroundColor: pt.BUTTON_PRIMARY,
      paddingHorizontal: PAYMENT_SPACING.xl,
      paddingVertical: PAYMENT_SPACING.md,
      borderRadius: 12,
    },
    backButtonText: {
      ...PAYMENT_TYPOGRAPHY.bodySmMedium,
      color: pt.BUTTON_PRIMARY_TEXT,
    },
    backLink: {
      paddingHorizontal: PAYMENT_SPACING.xl,
      paddingVertical: PAYMENT_SPACING.lg,
    },
    cardContainer: {
      paddingHorizontal: PAYMENT_SPACING.lg,
    },
    historyContainer: {
      paddingHorizontal: PAYMENT_SPACING.xl,
      paddingBottom: PAYMENT_SPACING.xxl,
    },
}), [pt]);

  const route = useRoute<MobilePaymentDetailScreenProps['route']>();
  const navigation = useNavigation<MobilePaymentDetailScreenProps['navigation']>();
  const { leaseId } = route.params;
  
  const authUser = useAppSelector((s) => (s as any).auth?.user);
  const userId = authUser?.id;

  const { balanceDue, balanceItems, accountCredit } = usePaymentBalance(leaseId);

  // Navigation handlers
  const {
    handleNavigate,
    handleLogoPress,
    handleSignOut,
    handleNotificationPress,
  } = useResidentScreen('PaymentDetail', navigation as any);

  const leaseQuery = useGetLeaseQuery(
    { leaseId, includeTransactions: false, includePayments: true },
    { skip: !leaseId }
  );

  const { isFetching, isError, isSuccess, data, currentData } = leaseQuery;

  // Get transaction history
  const { 
    rows: transactions, 
    isFetching: transactionsLoading,
    pendingPaymentsCount,
  } = useLeaseHistory(leaseId, userId);

  // State for transaction details modal
  const [selectedTransaction, setSelectedTransaction] = useState<HistoryRow | null>(null);
  const [showTransactionDetails, setShowTransactionDetails] = useState(false);

  const handleTransactionPress = (transaction: HistoryRow) => {
    setSelectedTransaction(transaction);
    setShowTransactionDetails(true);
  };

  const handleCloseTransactionDetails = () => {
    setShowTransactionDetails(false);
    setSelectedTransaction(null);
  };

  const handleBackToLeases = () => {
    navigation.goBack();
  };

  // Render header (used in all states)
  const renderHeader = () => (
    <ResidentMobileHeaderWithMenu
      onLogoPress={handleLogoPress}
      onSignOut={handleSignOut}
      onNavigate={handleNavigate}
      onNotificationPress={handleNotificationPress}
      currentPath="/payments"
      currentTitle="Payments"
      showNotification={true}
    />
  );

  // First load or lease switch only. A refetch of the same lease keeps
  // `currentData`, and the tree must stay up: the payment card hosts the
  // autopay modal, which would unmount mid-animation otherwise.
  if (isFetching && !currentData) {
    return (
      <View style={styles.container}>
        <StatusBar barStyle="light-content" />
        {renderHeader()}
        <MobileScreenLayout
          backgroundColor={pt.BACKGROUND}
          scrollable={false}
          lightStatusBar={false}
        >
          <View style={styles.loadingContainer}>
            <LoadingSpinner message="Loading lease details..." variant="admin" />
          </View>
        </MobileScreenLayout>
      </View>
    );
  }

  if (isError || !isSuccess || !data) {
    return (
      <View style={styles.container}>
        <StatusBar barStyle="light-content" />
        {renderHeader()}
        <MobileScreenLayout
          backgroundColor={pt.BACKGROUND}
          scrollable={false}
          lightStatusBar={false}
        >
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>Unable to load lease details</Text>
            <TouchableOpacity style={styles.backButton} onPress={handleBackToLeases}>
              <Text style={styles.backButtonText}>Go Back</Text>
            </TouchableOpacity>
          </View>
        </MobileScreenLayout>
      </View>
    );
  }

  const lease = selectLease(data);
  const property = lease?.property || {};
  const users = lease?.users || [];
  const scheduledCharges = lease?.scheduledCharges || [];
  const paymentsBlocked = !!lease?.paymentsBlocked;
  const autopayEnabled = hasAutoPay(lease);
  const unitNumberSpace = lease?.unitNumberSpace || lease?.unitNumber || '';

  // Determine if this is a connection lease
  const isConnection = !users?.some((u: any) => u?.id === authUser?.id);

  const connectedPrimary = users?.find((u: any) => u?.id !== authUser?.id && u?.LeaseCustomer?.customerType === 'primary');
  const displayUser = isConnection 
    ? (connectedPrimary || users?.find((u: any) => u?.id !== authUser?.id) || users?.[0] || {})
    : (users?.find((u: any) => u?.id === authUser?.id) || users?.[0] || {});

  const residentName = displayUser?.firstName && displayUser?.lastName
    ? `${displayUser.firstName} ${displayUser.lastName}`
    : 'Resident';

  // Check if current user is primary leaseholder
  const currentUserLease = users?.find((u: any) => u?.id === authUser?.id);
  const isPrimaryLeaseholder = currentUserLease?.LeaseCustomer?.customerType === 'primary';

  // Convert scheduled charges to RecurringCharge format
  const recurringCharges: RecurringCharge[] = scheduledCharges.map((charge: any) => ({
    name: charge.chargeCode || charge.description || 'Charge',
    amount: charge.amount ? Number(charge.amount) : null,
    frequency: 'Monthly',
  }));

  return (
    <View style={styles.container}>
      <StatusBar barStyle="light-content" />
      {renderHeader()}

      <MobileScreenLayout
        backgroundColor={pt.BACKGROUND}
        contentPadding={0}
        contentGap={0}
        lightStatusBar={false}
      >
        {/* Back to all leases link */}
        <BackLink
          label="Back to all leases"
          onPress={handleBackToLeases}
          color={pt.BLUE_LINK}
          style={styles.backLink}
        />

        {/* Payment Card */}
        <View style={styles.cardContainer}>
          <MobilePaymentCardV2
            leaseId={leaseId}
            residentName={residentName}
            propertyName={property?.marketingName || ''}
            unitNumber={unitNumberSpace}
            balanceDue={balanceDue}
            accountCredit={accountCredit}
            balanceItems={balanceItems}
            autopayEnabled={autopayEnabled}
            isConnection={isConnection}
            paymentsBlocked={paymentsBlocked}
            isPrimaryLeaseholder={isPrimaryLeaseholder}
            scheduledCharges={recurringCharges}
            showSeeDetails={false}
            firstName={displayUser?.firstName}
            lastName={displayUser?.lastName}
            photoUrl={displayUser?.photoUrl}
            onAutopayUpdated={() => leaseQuery.refetch()}
            pendingPaymentsCount={pendingPaymentsCount}
          />
        </View>

        {/* Transaction History */}
        <View style={styles.historyContainer}>
          <MobileTransactionHistoryTable
            transactions={transactions}
            isLoading={transactionsLoading}
            pageSize={10}
            onTransactionPress={handleTransactionPress}
          />
        </View>
      </MobileScreenLayout>

      {/* Transaction Details Modal */}
      <TransactionDetailsModal
        visible={showTransactionDetails}
        onClose={handleCloseTransactionDetails}
        transaction={selectedTransaction}
      />
    </View>
  );
};

